import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ApolloError,
  isApolloError,
  useLazyQuery,
  useMutation,
} from "@apollo/client";
import * as R from "ramda";
import ProfileService from "@app/service/profile";
import PatientService from "@app/service/patient";
import { NotificationContext, UppAuthContext } from "@app/provider";
import { HeapEventLocation } from "@app/provider/types";
import {
  Address,
  ChannelValue,
  Communication,
  CommunicationPayload,
  PatientEthnicityDTO,
  PersonalInformation,
  Profile,
  SetupProfileData,
  PatientBasicInfo,
} from "@app/provider/profile/types";
import { SuccessStatusResponse } from "@app/service/user";
import {
  getGraphQLErrorBodies,
  getGraphQLErrorCode,
} from "@app/provider/error/graphQLErrorCode";
import { routes } from "@app/routing";
import { useNavigate } from "react-router-dom";
import { ResultCodes } from "@app/service/resultCodes";
import { AddNotificationParameters } from "@app/provider/notification";
import { getExtendedServerErrorNotificationParams } from "@app/utils/getExtendedServerErrorNotificationParams";
import { formatProvinceCode } from "@app/utils/formatProvinceCode";

export interface VerifyProfileData {
  isVerified: boolean;
  intentToken?: string;
}

export interface ProfileController {
  isLoading: boolean;
  setupProfileIsLoading: boolean;
  setupProfileData?: Profile | undefined;
  setupProfileError?: string;
  confirmProfileIsLoading: boolean;
  confirmProfileData?: Profile | undefined;
  confirmProfileError?: string;
  getProfileIsLoading: boolean;
  getProfileData?: Profile | undefined;
  getProfileError?: string;
  verifyProfileByTokenData?: VerifyProfileData | null;
  verifyProfileByTokenIsLoading: boolean;
  verifyProfileByTokenError?: ApolloError;
  verifyProfileByToken: (
    dateOfBirth: string,
    token: string
  ) => Promise<VerifyProfileData | undefined>;
  getProfile: () => Promise<Profile | undefined>;
  setupProfile: (props: SetupProfileData) => Promise<Profile | undefined>;
  confirmProfile: (props: Profile) => Promise<Profile | undefined>;
  profileData?: Profile | undefined;
  profileDataIsLoading: boolean;
  updatePersonalInformation: (
    personalInformation: PersonalInformation
  ) => Promise<Profile | undefined>;
  updatePhone: (phoneNumber: string) => Promise<Profile | undefined>;
  updatePatientIsLoading: boolean;
  updatePatientError?: string;
  currentPatientAddress?: Address;
  setCurrentPatientAddress: Dispatch<SetStateAction<Address | undefined>>;
  addPatientAddress: (
    address: Partial<Address>,
    heapEventLocation?: HeapEventLocation
  ) => Promise<Address | undefined>;
  addPatientEthnicities: (
    patientEthnicities: PatientEthnicityDTO
  ) => Promise<PatientEthnicityDTO | undefined>;
  addPatientAddressIsLoading: boolean;
  addPatientEthnicitiesIsLoading: boolean;
  addPatientEthnicitiesError?: ApolloError;
  addPatientAddressError?: ApolloError;
  updatePatientAddress: (
    address: Partial<Address>
  ) => Promise<Address | undefined>;
  updatePatientAddressIsLoading: boolean;
  updatePatientAddressError?: ApolloError;
  updatePatientCommunication: (
    communication: Communication[],
    communicationPayload: CommunicationPayload
  ) => Promise<Communication[] | undefined>;
  updatePatientCommunicationIsLoading: boolean;
  updatePatientCommunicationError?: ApolloError;
  skipCommunicationPreferencesStep: boolean;
  sendPhoneNumberVerificationCode: (phoneNumber: string) => void;
  updatePatientPhoneNumber: (
    verificationCode: string
  ) => Promise<SuccessStatusResponse | undefined>;
  updatePatientLanguage: (
    language: string,
    token?: string
  ) => Promise<SuccessStatusResponse | undefined>;
  getPatientBasicInfo: () => Promise<PatientBasicInfo | undefined>;
  getPatientBasicInfoIsLoading: boolean;
  getPatientBasicInfoData?: PatientBasicInfo | undefined;
  getPatientBasicInfoError?: string;
}

export const Context = createContext<ProfileController>({
  isLoading: false,
  setupProfileIsLoading: false,
  verifyProfileByTokenData: undefined,
  verifyProfileByTokenIsLoading: false,
  getProfileIsLoading: false,
  confirmProfileIsLoading: false,
  verifyProfileByToken: async () => undefined,
  getProfile: async () => undefined,
  setupProfile: async () => undefined,
  confirmProfile: async () => undefined,
  profileData: undefined,
  profileDataIsLoading: false,
  updatePersonalInformation: async () => undefined,
  updatePhone: async () => undefined,
  updatePatientIsLoading: false,
  setCurrentPatientAddress: () => undefined,
  addPatientAddress: async () => undefined,
  addPatientEthnicities: async () => undefined,
  addPatientAddressIsLoading: false,
  addPatientEthnicitiesIsLoading: false,
  updatePatientAddress: async () => undefined,
  updatePatientAddressIsLoading: false,
  updatePatientCommunication: async () => undefined,
  updatePatientCommunicationIsLoading: false,
  skipCommunicationPreferencesStep: false,
  sendPhoneNumberVerificationCode: async () => undefined,
  updatePatientPhoneNumber: async () => undefined,
  updatePatientLanguage: async () => undefined,
  getPatientBasicInfo: async () => undefined,
  getPatientBasicInfoIsLoading: false,
  getPatientBasicInfoData: undefined,
  getPatientBasicInfoError: undefined,
});

Context.displayName = "ProfileContext";

const ProfileProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const { profile: uppProfile, setProfile } = useContext(UppAuthContext);
  const { addNotification, clear } = useContext(NotificationContext);
  const [profileData, setProfileData] = useState<Profile | undefined>();
  const [profileDataIsLoading, setProfileDataIsLoading] = useState(false);

  const [currentPatientAddress, setCurrentPatientAddress] = useState<
    Address | undefined
  >(undefined);

  const [
    setupProfile,
    {
      loading: setupProfileIsLoading,
      data: setupProfileData,
      error: setupProfileError,
    },
  ] = useMutation<{
    setupProfile: Profile;
  }>(ProfileService.setupProfile());

  const [
    confirmProfile,
    {
      loading: confirmProfileIsLoading,
      data: confirmProfileData,
      error: confirmProfileError,
    },
  ] = useMutation<{
    confirmProfile: Profile;
  }>(ProfileService.confirmProfile());

  const [
    verifyProfileByToken,
    {
      loading: verifyProfileByTokenIsLoading,
      error: verifyProfileByTokenError,
      data: verifyProfileByTokenData,
    },
  ] = useMutation<{
    verifyProfileByToken: { isVerified: boolean; intentToken?: string };
  }>(ProfileService.verifyProfileByToken());

  const [
    getProfile,
    {
      loading: getProfileIsLoading,
      data: getProfileData,
      error: getProfileError,
    },
  ] = useLazyQuery<{
    getProfile: Profile;
  }>(ProfileService.getProfile(), {
    fetchPolicy: "network-only",
  });

  const [
    updatePatient,
    {
      loading: updatePatientIsLoading,
      error: updatePatientError,
      data: updatePatientData,
    },
  ] = useMutation<{ updatePatient: Profile }>(PatientService.updatePatient());

  const [
    addPatientAddress,
    {
      loading: addPatientAddressIsLoading,
      data: addPatientAddressData,
      error: addPatientAddressError,
    },
  ] = useMutation<{ addPatientAddress: Address }>(
    ProfileService.addPatientAddress()
  );

  const [
    addPatientEthnicities,
    {
      loading: addPatientEthnicitiesIsLoading,
      data: addPatientEthnicitiesData,
      error: addPatientEthnicitiesError,
    },
  ] = useMutation<{ addPatientEthnicities: PatientEthnicityDTO }>(
    ProfileService.addPatientEthnicities()
  );

  const [
    updatePatientAddress,
    {
      loading: updatePatientAddressIsLoading,
      data: updatePatientAddressData,
      error: updatePatientAddressError,
    },
  ] = useMutation<{ updatePatientAddress: Address }>(
    ProfileService.updatePatientAddress()
  );

  const [
    updatePatientCommunication,
    {
      loading: updatePatientCommunicationIsLoading,
      data: updatePatientCommunicationData,
      error: updatePatientCommunicationError,
    },
  ] = useMutation<{ updatePatientCommunication: Communication[] }>(
    ProfileService.updatePatientCommunication(),
    {
      onError: (error) => {
        console.error(error);
        const notificationParams: AddNotificationParameters =
          getExtendedServerErrorNotificationParams(error);
        addNotification(notificationParams);
        handleGetProfile();
      },
    }
  );

  const [
    sendPhoneNumberVerificationCode,
    { loading: sendPhoneNumberVerificationCodeIsLoading },
  ] = useMutation<{
    sendPhoneNumberVerificationCode: SuccessStatusResponse;
  }>(ProfileService.sendPhoneNumberVerificationCode());

  const [
    updatePatientPhoneNumber,
    { loading: updatePatientPhoneNumberIsLoading },
  ] = useMutation<{
    updatePatientPhoneNumber: SuccessStatusResponse;
  }>(ProfileService.updatePatientPhoneNumber());

  const [updatePatientLanguage] = useMutation<{
    updatePatientLanguage: SuccessStatusResponse;
  }>(ProfileService.updatePatientLanguage());

  const [
    getPatientBasicInfo,
    {
      loading: getPatientBasicInfoIsLoading,
      data: getPatientBasicInfoData,
      error: getPatientBasicInfoError,
    },
  ] = useLazyQuery<{
    getPatientBasicInfo: PatientBasicInfo;
  }>(ProfileService.getPatientBasicInfo(), {
    fetchPolicy: "network-only",
  });

  const handleSetupProfile = async (
    profileData: Profile
  ): Promise<Profile | undefined> => {
    const transformedProfileData = {
      ...profileData,
      addresses: profileData.addresses?.map((address) => {
        const { uid, state, ...rest } = address;
        return {
          ...rest,
          provinceCode: state ? formatProvinceCode(state) : undefined,
        };
      }),
    };

    const profile = await setupProfile({
      variables: { profileData: transformedProfileData },
    });

    return profile.data?.setupProfile;
  };

  const handleConfirmProfile = async (
    profileData: Profile
  ): Promise<Profile | undefined> => {
    const transformedProfileData = {
      ...profileData,
      addresses: profileData.addresses?.map((address) => {
        const { uid, state, ...rest } = address;
        return {
          ...rest,
          provinceCode: state ? formatProvinceCode(state) : undefined,
        };
      }),
    };

    const profile = await confirmProfile({
      variables: { profileData: transformedProfileData },
    });

    return profile.data?.confirmProfile;
  };

  const handleVerifyProfileByToken = async (
    dateOfBirth: string,
    token: string
  ): Promise<VerifyProfileData | undefined> => {
    const profile = await verifyProfileByToken({
      variables: { dateOfBirth, token },
    });

    return profile.data?.verifyProfileByToken;
  };

  const handleGetProfile = async (): Promise<Profile | undefined> => {
    if (uppProfile?.patientUID) {
      const profile = await getProfile();

      if (profile.error) {
        console.error(profile.error);
        const notificationParams: AddNotificationParameters =
          getExtendedServerErrorNotificationParams(profile.error);
        addNotification(notificationParams);
      }
      if (
        profile.data?.getProfile.uid &&
        profile.data.getProfile.uid !== uppProfile.patientUID
      ) {
        setProfile({ ...uppProfile, patientUID: profile.data?.getProfile.uid });
      }
      return profile.data?.getProfile;
    }
  };

  const mergeProfileData = React.useCallback(
    (sourceProfile: Profile | undefined, newProfile: Profile | undefined) => {
      if (!sourceProfile) {
        return newProfile;
      }

      return newProfile
        ? (R.mergeDeepRight(sourceProfile, newProfile) as Profile)
        : sourceProfile;
    },
    []
  );

  useEffect(() => {
    setProfileData(
      mergeProfileData(
        getProfileData?.getProfile,
        confirmProfileData?.confirmProfile
      )
    );
  }, [getProfileData, confirmProfileData]);

  useEffect(() => {
    handleGetProfile();
  }, [
    addPatientAddressData,
    addPatientEthnicitiesData,
    updatePatientAddressData,
    updatePatientCommunicationData,
    updatePatientData,
  ]);

  useEffect(() => {
    const isLoading = getProfileIsLoading || confirmProfileIsLoading;
    setProfileDataIsLoading(isLoading);
  }, [getProfileIsLoading, confirmProfileIsLoading]);

  useEffect(() => {
    if (
      uppProfile &&
      !getProfileData?.getProfile &&
      !getProfileIsLoading &&
      uppProfile.patientUID
    ) {
      handleGetProfile();
    }
  }, [uppProfile]);

  const handleUpdatePersonalInformation = async (
    personalInformation: PersonalInformation
  ) => {
    const response = await updatePatient({
      variables: { patientData: personalInformation },
    });
    return response.data?.updatePatient;
  };

  const handleUpdatePhone = async (
    phoneNumber: string
  ): Promise<Profile | undefined> => {
    const patientData: Profile = { phone: phoneNumber };
    const response = await updatePatient({
      variables: { patientData },
    });
    return response.data?.updatePatient;
  };

  const handleAddPatientAddress = async (
    patientAddress: Partial<Address>,
    heapEventLocation?: HeapEventLocation
  ) => {
    const response = await addPatientAddress({
      variables: {
        patientAddress,
        heapEventLocation,
      },
    });

    return response.data?.addPatientAddress;
  };

  const handleAddPatientEthnicities = async (
    patientEthnicities: PatientEthnicityDTO
  ) => {
    const response = await addPatientEthnicities({
      variables: {
        patientEthnicities,
      },
    });

    return response.data?.addPatientEthnicities;
  };

  const handleUpdatePatientAddress = async (patientAddress: Address) => {
    const response = await updatePatientAddress({
      variables: {
        patientAddress,
      },
    });
    setCurrentPatientAddress(undefined);
    return response.data?.updatePatientAddress;
  };

  const handleUpdatePatientCommunication = async (
    patientCommunication: Communication[],
    communicationPayload: CommunicationPayload
  ) => {
    const response = await updatePatientCommunication({
      variables: {
        patientCommunication,
        communicationPayload,
      },
    });
    return response.data?.updatePatientCommunication;
  };

  const skipCommunicationPreferencesStep = useMemo(() => {
    const preferences = profileData?.communicationPreferences;
    if (!preferences) return false;

    // Server-derived: an opt-in on a channel the form actually collects.
    const hasOptInPreference = R.find(
      (pref) => Boolean(pref.optedIn),
      preferences
    );

    return Boolean(hasOptInPreference);
  }, [profileData?.communicationPreferences]);

  const handleSendPhoneNumberVerificationCode = async (phoneNumber: string) => {
    try {
      clear();
      await sendPhoneNumberVerificationCode({
        variables: {
          phoneNumber,
        },
      });
    } catch (error) {
      const notificationParams: AddNotificationParameters =
        getExtendedServerErrorNotificationParams(error);
      addNotification(notificationParams);
    }
  };

  const handleUpdatePatientPhoneNumber = async (verificationCode: string) => {
    clear();
    try {
      const response = await updatePatientPhoneNumber({
        variables: {
          verificationCode,
        },
      });
      if (response.data?.updatePatientPhoneNumber.success) {
        await getProfile();
        navigate(routes.contactDetails, {
          state: { phoneUpdated: true },
          replace: true,
        });
      }
      return response.data?.updatePatientPhoneNumber;
    } catch (error) {
      if (isApolloError(error) && error.graphQLErrors?.length > 0) {
        const incorrectActivationCodeError = getGraphQLErrorBodies(error).find(
          (e) => e.code === ResultCodes.INCORRECT_ACTIVATION_CODE
        );

        // throw it to show in verificationPhoneModal
        if (incorrectActivationCodeError) {
          throw incorrectActivationCodeError;
        } else {
          const notificationParams: AddNotificationParameters =
            getExtendedServerErrorNotificationParams(error);
          addNotification(notificationParams);
        }
      } else {
        addNotification({
          type: "error",
        });
      }
    }
  };

  const handleUpdatePatientLanguage = async (
    language: string,
    token?: string
  ) => {
    const response = await updatePatientLanguage({
      variables: {
        language,
        token,
      },
    });

    return response.data?.updatePatientLanguage;
  };

  const handleGetPatientBasicInfo = async (): Promise<
    PatientBasicInfo | undefined
  > => {
    const result = await getPatientBasicInfo();
    return result.data?.getPatientBasicInfo;
  };

  const isLoading =
    sendPhoneNumberVerificationCodeIsLoading ||
    updatePatientPhoneNumberIsLoading;

  const contextValue = useMemo(
    () => ({
      isLoading,
      profileData,
      profileDataIsLoading,
      getProfileIsLoading: getProfileIsLoading,
      getProfileData: getProfileData?.getProfile,

      setupProfileIsLoading: setupProfileIsLoading,
      setupProfileData: setupProfileData?.setupProfile,

      confirmProfileIsLoading: confirmProfileIsLoading,
      confirmProfileData: confirmProfileData?.confirmProfile,
      setupProfile: handleSetupProfile,

      verifyProfileByTokenData: verifyProfileByTokenData?.verifyProfileByToken,
      verifyProfileByTokenIsLoading,
      verifyProfileByTokenError,
      verifyProfileByToken: handleVerifyProfileByToken,
      getProfile: handleGetProfile,
      confirmProfile: handleConfirmProfile,
      updatePersonalInformation: handleUpdatePersonalInformation,
      updatePhone: handleUpdatePhone,
      updatePatientIsLoading,

      currentPatientAddress,
      setCurrentPatientAddress,
      addPatientAddress: handleAddPatientAddress,
      addPatientEthnicities: handleAddPatientEthnicities,
      addPatientAddressIsLoading,
      addPatientEthnicitiesIsLoading,
      addPatientEthnicitiesError,
      addPatientAddressError,
      updatePatientAddress: handleUpdatePatientAddress,
      updatePatientAddressIsLoading,
      updatePatientAddressError,
      updatePatientCommunication: handleUpdatePatientCommunication,
      updatePatientCommunicationIsLoading,
      updatePatientCommunicationError,
      skipCommunicationPreferencesStep,
      sendPhoneNumberVerificationCode: handleSendPhoneNumberVerificationCode,
      updatePatientPhoneNumber: handleUpdatePatientPhoneNumber,
      updatePatientLanguage: handleUpdatePatientLanguage,
      getPatientBasicInfo: handleGetPatientBasicInfo,
      getPatientBasicInfoIsLoading,
      getPatientBasicInfoData: getPatientBasicInfoData?.getPatientBasicInfo,
      getPatientBasicInfoError: getGraphQLErrorCode(getPatientBasicInfoError),
    }),
    [
      isLoading,
      profileData,
      profileDataIsLoading,
      getProfileIsLoading,
      getProfileData?.getProfile,

      setupProfileIsLoading,
      setupProfileData?.setupProfile,

      confirmProfileIsLoading,
      confirmProfileData?.confirmProfile,
      handleSetupProfile,

      verifyProfileByTokenData?.verifyProfileByToken,
      verifyProfileByTokenIsLoading,
      verifyProfileByTokenError,
      handleVerifyProfileByToken,
      handleGetProfile,
      handleConfirmProfile,
      handleUpdatePersonalInformation,
      handleUpdatePhone,
      updatePatientIsLoading,

      currentPatientAddress,
      setCurrentPatientAddress,
      handleAddPatientAddress,
      handleAddPatientEthnicities,
      addPatientAddressIsLoading,
      addPatientEthnicitiesIsLoading,
      addPatientEthnicitiesError,
      addPatientAddressError,
      handleUpdatePatientAddress,
      updatePatientAddressIsLoading,
      updatePatientAddressError,
      handleUpdatePatientCommunication,
      updatePatientCommunicationIsLoading,
      updatePatientCommunicationError,
      skipCommunicationPreferencesStep,
      handleSendPhoneNumberVerificationCode,
      handleUpdatePatientPhoneNumber,
      handleUpdatePatientLanguage,
      handleGetPatientBasicInfo,
      getPatientBasicInfoIsLoading,
      getPatientBasicInfoData?.getPatientBasicInfo,
      getPatientBasicInfoError,
    ]
  );

  return (
    <Context.Provider
      value={{
        ...contextValue,
        getProfileError: getGraphQLErrorCode(getProfileError),

        setupProfileError: getGraphQLErrorCode(setupProfileError),
        confirmProfileError: getGraphQLErrorCode(confirmProfileError),

        updatePatientError: getGraphQLErrorCode(updatePatientError),
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default ProfileProvider;
